import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  colorBackground,
  colorFieldsBorder,
  colorText,
  colorTextAdditional,
  colorAccent,
  colorRed,
  colorTextNew,
} from "../../../utils/palette";
import { borderRadius } from "../../../utils/styles";
import { DEFAULT_KPI_IDS } from "../kpis/kpiRegistry";
import KpiSlotsSection from "./KpiSlotsSection";
import GridMetricsSection from "./GridMetricsSection";
import {
  AddFormButton,
  FilterButton,
} from "../../../shared/components/TableElements";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  cursor: pointer;
`;

const Panel = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  background: ${colorBackground};
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.22s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const PanelHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${colorFieldsBorder};
  flex-shrink: 0;
`;

const PanelHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const PanelTitle = styled.h2`
  font-size: 17px;
  font-weight: 600;
  color: ${colorTextNew};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: none;
  color: ${colorTextAdditional};
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.15s;
  font-family: inherit;

  &:hover {
    background: ${colorFieldsBorder};
    color: ${colorText};
  }
`;

const PanelSubtitle = styled.p`
  font-size: 12px;
  color: ${colorTextAdditional};
  margin: 0;
  line-height: 1.4;
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const PanelFooter = styled.div`
  padding: 16px 24px 20px;
  border-top: 1px solid ${colorFieldsBorder};
  display: flex;
  align-items: center;
  justify-content: end;
  flex-shrink: 0;
  background: ${colorBackground};
`;

const ResetButton = styled.button`
  font-size: 12px;
  color: ${colorTextAdditional};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-family: inherit;
  padding: 0;

  &:hover {
    color: ${colorText};
  }
`;

const FooterActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelButton = styled.button`
  padding: 8px 18px;
  border: 1px solid ${colorFieldsBorder};
  border-radius: ${borderRadius};
  font-size: 13px;
  font-weight: 500;
  color: ${colorText};
  background: ${colorBackground};
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${colorText};
  }
`;

const SaveButton = styled.button`
  padding: 8px 24px;
  border: none;
  border-radius: ${borderRadius};
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: ${colorAccent};
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.88;
  }
`;

export default function CustomizerPanel({
  isOpen,
  onClose,
  activeKpiIds,
  activeGridIds,
  onSave,
}) {
  const { t } = useTranslation('common');
  const [draftKpiIds, setDraftKpiIds] = useState([]);
  const [draftGridIds, setDraftGridIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setDraftKpiIds([...activeKpiIds]);
      setDraftGridIds([...activeGridIds]);
      window.fcWidget?.hide();
    } else {
      window.fcWidget?.show();
    }
  }, [isOpen]);

  // useEffect(() => {
  //   return () => {
  //     window.fcWidget?.show();
  //   };
  // }, []);

  const canSave = draftKpiIds.length > 0 || draftGridIds.length > 0;

  if (!isOpen) return null;

  return (
    <Overlay>
      <Backdrop onClick={onClose} />
      <Panel>
        <PanelHeader>
          <PanelHeaderTop>
            <PanelTitle>{t('customize_dashboard')}</PanelTitle>
            <CloseButton onClick={onClose}>✕</CloseButton>
          </PanelHeaderTop>
          <PanelSubtitle>
            {t('customize_dashboard_subtitle')}
          </PanelSubtitle>
        </PanelHeader>

        <PanelBody>
          <KpiSlotsSection
            draftKpiIds={draftKpiIds}
            setDraftKpiIds={setDraftKpiIds}
          />
          <GridMetricsSection
            draftGridIds={draftGridIds}
            setDraftGridIds={setDraftGridIds}
          />
        </PanelBody>

        <PanelFooter>
          {/* <ResetButton onClick={() => setDraftKpiIds([...DEFAULT_KPI_IDS])}>
            {t('reset_kpi_bar')}
          </ResetButton> */}
          <FooterActions>
            <FilterButton type="button" onClick={onClose}>
              {t('cancel')}
            </FilterButton>
            <AddFormButton
              type="button"
              disabled={!canSave}
              title={!canSave ? t('select_at_least_one_metric') : undefined}
              onClick={canSave ? () => onSave(draftKpiIds, draftGridIds) : undefined}
            >
              {t('save')}
            </AddFormButton>
          </FooterActions>
        </PanelFooter>
      </Panel>
    </Overlay>
  );
}
